import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { tokens } from '../../../../../theme';
import QuestionSlot from '../components/QuestionSlot';
import QuestionNav from '../components/QuestionNav';
import { useTestSession } from '../TestSessionContext';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Render at 2x minimum so PDFs stay crisp on Retina / high-DPI screens
const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

const PdfRenderer = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {
        test, answers,
        currentSectionIndex, setCurrentSectionIndex,
        currentQuestionIndex, setCurrentQuestionIndex,
        isDirty, save,
    } = useTestSession();

    const [numPages, setNumPages] = useState(null);
    const [dividerPosition, setDividerPosition] = useState(5);
    const rightPaneRef = useRef(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('v2TestViewMode') || 'all');

    const toggleViewMode = () => {
        const next = viewMode === 'single' ? 'all' : 'single';
        setViewMode(next);
        localStorage.setItem('v2TestViewMode', next);
    };

    const fileUrl = test?.file_url;
    const sections = test?.v2_test_sections || [];
    const currentSection = sections[currentSectionIndex];
    const questions = currentSection?.v2_test_questions || [];
    const currentQuestion = questions[currentQuestionIndex];
    const sectionTotal = questions.length;

    // Split-pane resizer
    const handleMouseDown = useCallback(() => {
        const handleMouseMove = (e) => {
            if (rightPaneRef.current) {
                const newPos = (e.clientX / window.innerWidth) * 10;
                setDividerPosition(Math.max(2, Math.min(8, newPos)));
            }
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    // Navigation
    const goTo = useCallback((sIdx, qIdx) => {
        if (isDirty) save();
        setCurrentSectionIndex(sIdx);
        setCurrentQuestionIndex(qIdx);
    }, [isDirty, save, setCurrentSectionIndex, setCurrentQuestionIndex]);

    const goPrev = () => {
        if (currentQuestionIndex > 0) goTo(currentSectionIndex, currentQuestionIndex - 1);
        else if (currentSectionIndex > 0) {
            const prevQs = sections[currentSectionIndex - 1]?.v2_test_questions || [];
            goTo(currentSectionIndex - 1, prevQs.length - 1);
        }
    };

    const goNext = () => {
        if (currentQuestionIndex < questions.length - 1) goTo(currentSectionIndex, currentQuestionIndex + 1);
        else if (currentSectionIndex < sections.length - 1) goTo(currentSectionIndex + 1, 0);
    };

    const isFirst = currentSectionIndex === 0 && currentQuestionIndex === 0;
    const isLast = currentSectionIndex === sections.length - 1 && currentQuestionIndex === questions.length - 1;

    // Keyboard nav
    useEffect(() => {
        if (viewMode !== 'single') return;
        const handler = (e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); document.activeElement?.blur(); goPrev(); }
            if (e.key === 'ArrowRight') { e.preventDefault(); document.activeElement?.blur(); goNext(); }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    });

    // Block print
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'v2-pdf-print-block';
        style.textContent = '@media print { body * { visibility: hidden !important; } }';
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    // Flagged questions for progress bar markers
    const flaggedQuestions = questions
        .map((q, idx) => answers.get(q.id)?.flagged ? { id: q.id, index: idx } : null)
        .filter(Boolean);

    if (!currentQuestion && viewMode === 'single') return null;

    return (
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Left pane — PDF */}
            <Box
                sx={{ flex: dividerPosition, overflow: 'auto', p: 1, minWidth: 200, userSelect: 'none', WebkitUserSelect: 'none' }}
                onContextMenu={(e) => e.preventDefault()}
            >
                {fileUrl ? (
                    <Document
                        file={fileUrl}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                        loading={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
                        error={<Typography p={2} color="error">Failed to load PDF</Typography>}
                    >
                        {numPages && Array.from({ length: numPages }, (_, i) => (
                            <Page key={i + 1} pageNumber={i + 1} scale={pixelRatio} renderTextLayer={false} renderAnnotationLayer={false} />
                        ))}
                    </Document>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No PDF available</Typography>
                    </Box>
                )}
            </Box>

            {/* Resizer */}
            <Box
                onMouseDown={handleMouseDown}
                sx={{
                    width: 8,
                    cursor: 'col-resize',
                    bgcolor: 'divider',
                    '&:hover': { bgcolor: colors.blueAccent[500] },
                    transition: 'background-color 0.15s',
                    flexShrink: 0,
                    zIndex: 10,
                    position: 'relative',
                }}
            />

            {/* Right pane */}
            <Box ref={rightPaneRef} sx={{ flex: 10 - dividerPosition, display: 'flex', flexDirection: 'column', minWidth: 300 }}>
                <QuestionNav
                    sections={sections}
                    currentSectionIndex={currentSectionIndex}
                    onSectionNavigate={(idx) => goTo(idx, 0)}
                    answers={answers}
                    viewMode={viewMode}
                    onToggleViewMode={toggleViewMode}
                    currentQuestionIndex={currentQuestionIndex}
                    sectionTotal={sectionTotal}
                    isFirst={isFirst}
                    isLast={isLast}
                    onPrev={goPrev}
                    onNext={goNext}
                    onProgressClick={(idx) => goTo(currentSectionIndex, idx)}
                    flaggedQuestions={flaggedQuestions}
                    onFlagClick={(idx) => goTo(currentSectionIndex, idx)}
                />

                {/* Question content */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
                    {viewMode === 'single' ? (
                        <Box key={`${currentSectionIndex}-${currentQuestionIndex}`}
                            sx={{ '@keyframes fadeSlideIn': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                                animation: 'fadeSlideIn 0.15s ease-out' }}>
                            <QuestionSlot question={currentQuestion} allQuestionsInSection={questions} />
                        </Box>
                    ) : (
                        questions.map(q => (
                            <QuestionSlot key={q.id} question={q} allQuestionsInSection={questions} />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default PdfRenderer;
